import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaBook,
  FaHeadphones,
  FaPencilAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLayerGroup,
  FaUpload,
  FaSpinner,
  FaChevronRight,
  FaClock,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ExamSet = {
  id: number;
  setName: string;
  level: string;
  variant: number;
  lesenDuration: number;
  horenDuration: number;
  schreibenDuration: number;
  totalDurationMinutes: number;
  horenAudioUrl: string | null;
  horenAudioStreamUrl: string | null;
  totalQuestions: number;
  totalPoints: number;
};

type ExamPart = {
  id: number;
  module: string;
  partNumber: number;
  displayName: string;
  instructions: string;
  partTitle: string;
  totalPoints: number;
  questionCount: number;
  isActive: boolean;
};

const AdminExamBuilder: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();

  const [examSet, setExamSet] = useState<ExamSet | null>(null);
  const [examParts, setExamParts] = useState<ExamPart[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePartModal, setShowCreatePartModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string>("LESEN");
  const [creatingPart, setCreatingPart] = useState(false);
  const [horenAudioFile, setHorenAudioFile] = useState<File | null>(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const [newPart, setNewPart] = useState({
    module: "LESEN",
    partNumber: 1,
    instructions: "",
    partTitle: "",
  });

  useEffect(() => {
    fetchExamData();
  }, [examId]);

  const fetchExamData = async () => {
    setLoading(true);
    try {
      // Fetch exam set
      const examRes = await fetch(`${API_BASE_URL}/api/admin/exams/${examId}`, {
        credentials: "include",
      });
      if (examRes.ok) {
        const examResult = await examRes.json();
        setExamSet(examResult.data || examResult);
      }

      // Fetch all parts
      const partsRes = await fetch(
        `${API_BASE_URL}/api/admin/exams/${examId}/parts`,
        { credentials: "include" }
      );
      if (partsRes.ok) {
        const partsResult = await partsRes.json();
        setExamParts(partsResult.data || partsResult || []);
      }
    } catch (err) {
      console.error("Error fetching exam data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePart = async () => {
    if (!newPart.partNumber || !newPart.instructions) {
      alert("Please provide part number and instructions");
      return;
    }

    setCreatingPart(true);
    try {
      const modulePartsFiltered = examParts.filter(
        (p) => p.module === newPart.module
      );
      const maxDisplayOrder =
        modulePartsFiltered.length > 0
          ? Math.max(...modulePartsFiltered.map((p) => p.partNumber))
          : 0;

      const payload = {
        examSetId: parseInt(examId!),
        module: newPart.module,
        partNumber: newPart.partNumber,
        displayOrder: maxDisplayOrder + 1,
        instructions: newPart.instructions,
        partTitle: newPart.partTitle || null,
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/exam-parts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowCreatePartModal(false);
        await fetchExamData();
        setNewPart({
          module: selectedModule,
          partNumber: 1,
          instructions: "",
          partTitle: "",
        });
      } else {
        const data = await response.json();
        alert(data.message || "Failed to create Teil");
      }
    } catch (err) {
      console.error("Error creating Teil:", err);
      alert("Network error. Please try again.");
    } finally {
      setCreatingPart(false);
    }
  };

  const handleDeletePart = async (partId: number) => {
    if (
      !window.confirm(
        "Are you sure? This will delete all questions in this Teil."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exam-parts/${partId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        await fetchExamData();
      } else {
        alert("Failed to delete Teil");
      }
    } catch (err) {
      console.error("Error deleting Teil:", err);
      alert("Network error. Please try again.");
    }
  };

  const handleUploadAudio = async () => {
    if (!horenAudioFile) {
      alert("Please select an audio file first");
      return;
    }

    setUploadingAudio(true);
    try {
      const formData = new FormData();
      formData.append("file", horenAudioFile);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/${examId}/upload-horen-audio`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (response.ok) {
        setHorenAudioFile(null);
        await fetchExamData();
      } else {
        const data = await response.json();
        alert(data.message || "Failed to upload audio");
      }
    } catch (err) {
      console.error("Error uploading audio:", err);
      alert("Network error. Please try again.");
    } finally {
      setUploadingAudio(false);
    }
  };

  const getModuleParts = (module: string) => {
    return examParts
      .filter((p) => p.module === module)
      .sort((a, b) => a.partNumber - b.partNumber);
  };

  const getModuleStats = (module: string) => {
    const parts = getModuleParts(module);
    const totalQuestions = parts.reduce((sum, p) => sum + p.questionCount, 0);
    const totalPoints = parts.reduce((sum, p) => sum + p.totalPoints, 0);
    return {
      parts: parts.length,
      questions: totalQuestions,
      points: totalPoints,
    };
  };

  const isModuleComplete = (module: string) => {
    const parts = getModuleParts(module);
    return parts.length > 0 && parts.every((p) => p.questionCount > 0);
  };

  const getTotalProgress = () => {
    const modules = ["LESEN", "HOREN", "SCHREIBEN"];
    const completed = modules.filter((m) => isModuleComplete(m)).length;
    return Math.round((completed / modules.length) * 100);
  };

  const openCreatePartModal = (module: string) => {
    const modulePartsFiltered = examParts.filter((p) => p.module === module);
    const nextPartNumber =
      modulePartsFiltered.length > 0
        ? Math.max(...modulePartsFiltered.map((p) => p.partNumber)) + 1
        : 1;

    setSelectedModule(module);
    setNewPart({
      module,
      partNumber: nextPartNumber,
      instructions: "",
      partTitle: "",
    });
    setShowCreatePartModal(true);
  };

  const renderModuleCard = (
    module: string,
    icon: React.ReactNode,
    color: string,
    duration: number
  ) => {
    const parts = getModuleParts(module);
    const stats = getModuleStats(module);
    const complete = isModuleComplete(module);
    const hasAudio = module === "HOREN" ? examSet?.horenAudioUrl : true;

    return (
      <div
        className={`bg-white rounded-xl shadow-lg border-l-4 ${color} overflow-hidden`}
      >
        {/* Header */}
        <div
          className={`bg-gradient-to-r ${color.replace(
            "border-",
            "from-"
          )}-50 ${color.replace(
            "border-",
            "to-"
          )}-100 px-6 py-4 border-b border-gray-200`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 ${color.replace(
                  "border-",
                  "bg-"
                )} text-white rounded-lg flex items-center justify-center`}
              >
                {icon}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  {module === "LESEN" && "Lesen (Reading)"}
                  {module === "HOREN" && "Hören (Listening)"}
                  {module === "SCHREIBEN" && "Schreiben (Writing)"}
                  {complete && (
                    <FaCheckCircle className="text-green-600 text-xl" />
                  )}
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                  <span className="flex items-center gap-1">
                    <FaClock className="text-gray-400" />
                    {duration} min
                  </span>
                  <span>{stats.parts} Teile</span>
                  <span>{stats.questions} Questions</span>
                  <span>{stats.points} Points</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => openCreatePartModal(module)}
              className={`px-4 py-2 ${color.replace(
                "border-",
                "bg-"
              )} text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2`}
            >
              <FaPlus />
              Add Teil
            </button>
          </div>
        </div>

        {/* Hören Audio Warning */}
        {module === "HOREN" && !hasAudio && (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <FaExclamationTriangle className="text-yellow-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-yellow-800 font-semibold">
                  ⚠️ No audio uploaded yet
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Upload the Hören audio file before adding questions
                </p>
                <div className="mt-3 space-y-2">
                  <input
                    type="file"
                    accept="audio/*,video/mp4"
                    onChange={(e) =>
                      setHorenAudioFile(e.target.files?.[0] || null)
                    }
                    className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                  {horenAudioFile && (
                    <button
                      onClick={handleUploadAudio}
                      disabled={uploadingAudio}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {uploadingAudio ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Upload Audio
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Teile List */}
        <div className="p-6">
          {parts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FaLayerGroup className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p className="text-sm">No Teile created yet</p>
              <p className="text-xs mt-1">Click "Add Teil" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {parts.map((part) => (
                <div
                  key={part.id}
                  className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center justify-center w-10 h-10 ${color.replace(
                          "border-",
                          "bg-"
                        )}-100 ${color.replace(
                          "border-",
                          "text-"
                        )}-800 font-bold rounded-lg`}
                      >
                        {part.partNumber}
                      </span>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {part.displayName}
                          {part.partTitle && (
                            <span className="text-gray-600 text-sm ml-2">
                              - {part.partTitle}
                            </span>
                          )}
                        </h4>
                        <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                          <span>{part.questionCount} questions</span>
                          <span>{part.totalPoints} points</span>
                          {part.questionCount === 0 && (
                            <span className="text-yellow-600 font-semibold flex items-center gap-1">
                              <FaExclamationTriangle />
                              No questions yet
                            </span>
                          )}
                          {part.questionCount > 0 && (
                            <span className="text-green-600 font-semibold flex items-center gap-1">
                              <FaCheckCircle />
                              Complete
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        navigate(`/admin/exams/${examId}/questions`)
                      }
                      className={`px-3 py-2 ${color.replace(
                        "border-",
                        "bg-"
                      )} text-white rounded-lg font-semibold hover:shadow-md transition-all text-sm flex items-center gap-1`}
                    >
                      {part.questionCount === 0 ? (
                        <>
                          <FaPlus size={12} />
                          Add Questions
                        </>
                      ) : (
                        <>
                          <FaEdit size={12} />
                          Edit Questions
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeletePart(part.id)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors text-sm"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  if (!examSet) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-red-600">Exam set not found</p>
      </div>
    );
  }

  const progress = getTotalProgress();

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaLayerGroup />}
        title="Exam Builder"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exams", to: "/admin/exam-management" },
          { label: examSet.setName },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Exam Info Card */}
        <div className="mb-6 bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {examSet.setName}
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                  {examSet.level}
                </span>
                <span>Variant {examSet.variant}</span>
                <span className="flex items-center gap-1">
                  <FaClock />
                  {examSet.totalDurationMinutes} minutes
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-[#009fe3]">
                {examSet.totalQuestions}
              </div>
              <div className="text-sm text-gray-600">Total Questions</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Overall Progress
              </span>
              <span className="text-sm font-bold text-[#009fe3]">
                {progress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-[#009fe3] to-cyan-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {progress === 100
                ? "✅ All modules complete! Ready to assign to students."
                : "Complete all modules to finish exam setup"}
            </p>
          </div>
        </div>

        {/* Module Cards */}
        <div className="space-y-6">
          {renderModuleCard(
            "LESEN",
            <FaBook size={24} />,
            "border-blue-600",
            examSet.lesenDuration
          )}
          {renderModuleCard(
            "HOREN",
            <FaHeadphones size={24} />,
            "border-purple-600",
            examSet.horenDuration
          )}
          {renderModuleCard(
            "SCHREIBEN",
            <FaPencilAlt size={24} />,
            "border-green-600",
            examSet.schreibenDuration
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={() => navigate("/admin/exam-management")}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            ← Back to Exams
          </button>
          {progress === 100 && (
            <button
              onClick={() => navigate(`/admin/exams/${examId}/assign`)}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <FaCheckCircle />
              Exam Complete - Assign to Students
              <FaChevronRight />
            </button>
          )}
        </div>
      </main>

      {/* Create Teil Modal */}
      {showCreatePartModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
          onClick={() => setShowCreatePartModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FaLayerGroup />
                Create New Teil for {selectedModule}
              </h3>
              <button
                onClick={() => setShowCreatePartModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FaTimes size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Teil Number *
                </label>
                <input
                  type="number"
                  value={newPart.partNumber}
                  onChange={(e) =>
                    setNewPart({
                      ...newPart,
                      partNumber: parseInt(e.target.value) || 1,
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  min="1"
                  max="10"
                  style={{ fontSize: "16px" }}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Teil Title (Optional)
                </label>
                <input
                  type="text"
                  value={newPart.partTitle}
                  onChange={(e) =>
                    setNewPart({ ...newPart, partTitle: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  placeholder="e.g., Informationen verstehen"
                  style={{ fontSize: "16px" }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Instructions (Aufgabenstellung) *
                </label>
                <textarea
                  value={newPart.instructions}
                  onChange={(e) =>
                    setNewPart({ ...newPart, instructions: e.target.value })
                  }
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  placeholder="Sie lesen in einer Zeitung diesen Text. Wählen Sie für die Aufgaben 1-5 die richtige Lösung."
                  style={{ fontSize: "16px" }}
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleCreatePart}
                  disabled={
                    creatingPart || !newPart.partNumber || !newPart.instructions
                  }
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {creatingPart ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle />
                      Create Teil
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowCreatePartModal(false)}
                  disabled={creatingPart}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExamBuilder;
